import { useSyncExternalStore } from 'react';

/**
 * מה רואים במסך רחב.
 *
 * עיקרון 4 במסמך התכנון: מסכי העובד נבנים לרוחב 375, ומסכי הניהול
 * נבנים לשולחן העבודה. מכאן נובעות שתי ההחלטות כאן:
 *
 * 1. מנהל שנכנס במסך רחב מגיע לקונסולה. עד עכשיו הוא נחת באפליקציית
 *    העובד, שהיא עמודה צרה באמצע מסך ריק, והיה צריך למצוא את /admin
 *    בעצמו.
 * 2. מי שאינו מנהל ממשיך לראות את אפליקציית העובד, אבל ממוסגרת
 *    ובהקשר, ולא כעמודה שנשכחה. היא לא מתיימרת להיות מסך דסקטופ.
 *
 * ההפניה היא רק מהבית. קישור ישיר למסך עובד נפתח במסך עובד, כי מי
 * שלחץ עליו התכוון אליו.
 */

/** `lg` של Tailwind. אותה נקודה שבה הקונסולה עוברת לסרגל צד קבוע. */
const WIDE = '(min-width: 64rem)';

/**
 * מנהל שבחר במפורש את אפליקציית העובד. נשמר לאורך הסשן, אחרת רענון
 * היה מחזיר אותו לקונסולה בניגוד למה שביקש.
 *
 * sessionStorage ולא state: הבחירה צריכה לשרוד רענון, ולא צריכה
 * לשרוד סגירת חלון. היא גם לא מעניינת אף אחד חוץ מהדפדפן הזה.
 */
const PREFERS_APP = 'prefers-employee-app';

/** בחלון פרטי או עם אחסון חסום, הקריאה זורקת. ברירת המחדל היא קונסולה. */
export function preferEmployeeApp(): void {
  try {
    sessionStorage.setItem(PREFERS_APP, '1');
  } catch {
    // אין איפה לזכור. המנהל יחזור לקונסולה ברענון, וזו פגיעה נסבלת.
  }
}

export function prefersEmployeeApp(): boolean {
  try {
    return sessionStorage.getItem(PREFERS_APP) === '1';
  } catch {
    return false;
  }
}

/**
 * ההחלטה עצמה, כפונקציה טהורה מלבד קריאת ההעדפה. מופרדת מהקומפוננטה
 * כדי שתהיה ניתנת לבדיקה בלי ראוטר ובלי הקשר.
 */
export function shouldEnterConsole(input: {
  isManager: boolean;
  isWide: boolean;
}): boolean {
  return input.isManager && input.isWide && !prefersEmployeeApp();
}

function subscribe(onChange: () => void): () => void {
  const query = window.matchMedia(WIDE);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

/**
 * האם המסך רחב, כערך תגובתי. נדרש כי ההחלטה אינה רק עיצוב ב CSS אלא
 * גם ניתוב, ומסך שמשתנה באמצע (סיבוב טאבלט, גרירת חלון) צריך להגיע
 * לאותה תוצאה כמו טעינה טרייה.
 */
export function useIsWide(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(WIDE).matches,
    () => false,
  );
}
